<template>
  <booking-block
    class="booking-block--payment"
    :style="{ opacity: paymentwindowReady ? '1' : '0' }"
    :editMode="true"
    :title="translations.header"
    :subTitle="translations.sub_header"
    :isCollapsed="false"
    :valid="!isNotValid"
    :showSaveBtn="false"
    :showEditBtn="false"
  >
    <template slot="edit">
      <iframe
        id="paymentFrame"
        name="paymentFrame"
        frameborder="0"
        height="600px"
        style="width: 100%"
        :src="paymentFrameworkUrl"
      >
      </iframe>
      <msg-box type="info" v-for="(note, key) in paymentNotes" :key="key">
        <p v-text="note.text"></p>
      </msg-box>
    </template>
  </booking-block>
</template>

<script>
import BookingBlock from "./booking-block.vue";
import Moment from "moment";
import MsgBox from "../msg-block.vue";
import _ from "underscore";
import * as Sentry from "@sentry/vue";
import { isElementInViewport } from "./../../utils";
import { scrollIntoView } from "scroll-js";

let TokenUpdateTimer = null;
let PaymentWindowProxy = null;

export default {
  props: {
    customer: {
      type: Object,
      default() {
        return {
          firstName: "",
          lastName: "",
          address: "",
          zipCode: "",
          city: "",
          countryCode: "DK",
          email: "",
          phone: "",
        };
      },
    },
  },
  data() {
    return {
      translations: APP_CONFIG.TRANSLATION.BOOKING_PAYMENT,
      errors: {},
      paymentwindowReady: false,
      paymentTokens: [],
      intCustoemrData: {},
      confirmInitialData: false,
    };
  },
  mounted() {
    this.intCustoemrData = Object.assign({}, this.customer);
    if (!PaymentWindowProxy) {
      this.$nextTick(() => {
        PaymentWindowProxy = new Porthole.WindowProxy(
          APP_CONFIG.PAYMENT_FRAMEWORK_PROXY,
          "paymentFrame"
        );
        PaymentWindowProxy.addEventListener(this.onProxyMessage);
      });
    }
  },
  beforeDestroy() {
    if (TokenUpdateTimer) {
      clearTimeout(TokenUpdateTimer);
    }

    if (PaymentWindowProxy) {
      PaymentWindowProxy.removeEventListener(this.onProxyMessage);
      PaymentWindowProxy = null;
    }
  },
  methods: {
    onProxyMessage(event) {
      switch (event.data.action) {
        case "ready":
          this.onPaymentFrameworkReady(event);
          break;
        case "token":
          this.onPaymentTokensReceived(event.data.body);
          break;
        case "resize":
          this.onPaymentFrameResize(event.data.body);
          break;
        case "confirmInitalData":
          this.onPaymentConfirmInitalData(event);
          break;
        default:
          this.noop(event);
          break;
      }
    },
    noop(event) {
      Sentry.captureMessage("onPaymentNoop", {
        level: "info",
        extra: { event: JSON.stringify(event) },
      });
      console.log("Unknown event recived", event);
    },
    onPaymentConfirmInitalData(event) {
      Sentry.captureMessage("onPaymentConfirmInitalData", {
        level: "info",
        extra: { event: JSON.stringify(event) },
      });
      console.log("Paymentdata confirmed", event);
      this.confirmInitialData = true;
    },
    onPaymentFrameResize(body) {
      console.log("Changing size");
      Sentry.captureMessage("onPaymentFrameResize", {
        level: "info",
        extra: { body: JSON.stringify(body) },
      });
      document.getElementById("paymentFrame").height = body.frameHeight;

      //If paymentframe is not visible - scroll it into thew view area
      this.$nextTick(() => {
        if (!isElementInViewport(this.$el) && window.innerWidth < 900) {
          let scrollContainer =
            document.documentElement.scrollTop > 0
              ? document.documentElement
              : document.body;
          scrollIntoView(this.$el, scrollContainer);
        }
      });
    },
    onPaymentFrameworkReady(event) {
      console.log("Payment ready", event);
      Sentry.captureMessage("onPaymentFrameworkReady", {
        level: "info",
        extra: { event: JSON.stringify(event) },
      });
      this.paymentwindowReady = true;
      this.$emit("ready", true);
      this.$nextTick(() => {
        if (!this.confirmInitialData) {
          this.postInitialData();
        }
      });
    },
    onPaymentTokensReceived(body) {
      if (!this.confirmInitialData) {
        console.error("Intial data has not been confirmed!");
        Sentry.captureMessage("OnPaymentTokensReceivedNoIntialDataConfirmed", {
          level: "info",
          tags: {
            showPaymentwindow: true,
          },
          extra: { body: JSON.stringify(body) },
        });

        return;
      }

      console.log("payment tokens returned", body.tokenList);
      Sentry.captureMessage("OnPaymentTokensReceived", {
        level: "info",
        tags: {
          showPaymentwindow: true,
        },
        extra: { body: JSON.stringify(body) },
      });

      this.paymentTokens = body.tokenList ? body.tokenList : [];
      this.$emit("paymenttokens", this.paymentTokens);
      if (TokenUpdateTimer) {
        clearTimeout(TokenUpdateTimer);
        TokenUpdateTimer = null;
      }

      TokenUpdateTimer = setTimeout(() => {
        if (this.paymentTokens.length) {
          this.$emit("booktravel");
        }
      }, 1000);
    },
    postInitialData() {
      if (this.paymentwindowReady && PaymentWindowProxy) {
        let body = this.getPaymentBody();

        console.debug("posting initialData", body);
        Sentry.captureMessage("PaymentFrameUpdatePaymentData", {
          level: "info",
          extra: {
            body: JSON.stringify(body),
          },
        });

        PaymentWindowProxy.post({
          action: "initalData",
          body,
        });
      }
    },
    getPaymentMethods() {
      let paymentTypes =
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "paymentTypes",
        ]) || [];

      if (!paymentTypes) {
        return [];
      }

      let paymentMethods = paymentTypes
        .filter(({ type }) => type !== "credit card")
        .map(({ code }) => code);

      if (this.getCreditCardTypes().length > 0) {
        paymentMethods.push("CC");
      }

      return paymentMethods;
    },
    getFinalPaymentCurrency() {
      return (
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "totalPriceEuro",
          "currency",
        ]) || "DKK"
      );
    },
    getFinalPaymentPrice() {
      return (
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "totalPriceEuro",
          "value",
        ]) || 0
      );
    },
    getTourOperatorCode() {
      return (
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "tourOperator",
          "code",
        ]) || ""
      );
    },
    getTravelDate() {
      return (
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "travelDate",
        ]) || {}
      );
    },
    getCreditCardTypes() {
      let paymenttypes =
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "paymentTypes",
        ]) || [];

      if (!paymenttypes) {
        return [];
      }

      return paymenttypes
        .filter(({ type }) => type === "credit card")
        .map(({ code }) => code);
    },
    getFlightOutboundLegList() {
      let outboundleglist =
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "flightOffer",
          "flight",
          "outboundLegList",
        ]) || [];

      return outboundleglist.map(
        ({
          departureAirport,
          arrivalAirport,
          arrivalDateTime,
          departureDateTime,
          flightCarrier,
          flightNumber,
        }) => {
          return {
            departureAirportCode: departureAirport.code,
            arrivalAirportCode: arrivalAirport.code,
            departureDate: Moment(departureDateTime).format("YYYY-MM-DD"),
            arrivalDate: Moment(arrivalDateTime).format("YYYY-MM-DD"),
            departureTime: Moment(departureDateTime).format("HH:mm"),
            flightCarrierCode: flightCarrier.code,
            flightNumber,
          };
        }
      );
    },
    getFlightInBoundLegList() {
      let inboundLegList =
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "packageOffer",
          "flightOffer",
          "flight",
          "inboundLegList",
        ]) || [];

      return inboundLegList.map(
        ({
          departureAirport,
          arrivalAirport,
          arrivalDateTime,
          departureDateTime,
          flightCarrier,
          flightNumber,
        }) => {
          return {
            departureAirportCode: departureAirport.code,
            arrivalAirportCode: arrivalAirport.code,
            departureDate: Moment(departureDateTime).format("YYYY-MM-DD"),
            arrivalDate: Moment(arrivalDateTime).format("YYYY-MM-DD"),
            departureTime: Moment(departureDateTime).format("HH:mm"),
            flightCarrierCode: flightCarrier.code,
            flightNumber,
          };
        }
      );
    },
    getPaymentBody() {
      let travelDate = this.getTravelDate();
      const phonePrefix = (this.intCustoemrData.phone) ? this.intCustoemrData.phone.slice(1, 3) : '';
      const phone = (this.intCustoemrData.phone) ? this.intCustoemrData.phone.slice(3) : '';

      return {
        products: [
          {
            id: "Package",
            label: " ",
            paymentMethods: this.getPaymentMethods(),
            currency: this.getFinalPaymentCurrency(),
            amount: this.getFinalPaymentPrice(),
            operator: this.getTourOperatorCode(),
            firstname: this.intCustoemrData.firstName,
            lastname: this.intCustoemrData.lastName,
            phone: this.intCustoemrData.phone,
            email: this.intCustoemrData.email,
            countryCode: this.intCustoemrData.countryCode,
            beginDate: travelDate.fromDate,
            endDate: travelDate.toDate,
            creditCardTypes: this.getCreditCardTypes(),
            flightOutbound: {
              travelClass: "economy",
              legList: this.getFlightOutboundLegList(),
            },
            flightInbound: {
              travelClass: "economy",
              legList: this.getFlightInBoundLegList(),
            },
          },
        ],
        logUserInteraction: true,
        prefill: {
          firstname: this.intCustoemrData.firstName,
          lastname: this.intCustoemrData.lastName,
          phoneSubscriber: phone,
          phonePrefix,
          phoneType: "mobile",
          email: this.intCustoemrData.email,
          countryCode: this.intCustoemrData.countryCode,
          creditCardTypes: this.getCreditCardTypes(),
          addressLine1: this.intCustoemrData.address,
          city: this.intCustoemrData.city,
          zipCode: this.intCustoemrData.zipCode,
        }
      };
    },
  },
  computed: {
    isNotValid() {
      let errors = {};

      if (!this.paymentTokens.length) {
        errors["payment"] = [this.translations.ERRORS.missing_payment_info];
      }

      return !_.isEmpty(errors) ? errors : null;
    },
    paymentNotes() {
      let paymentNotes =
        _.propertyOf(this.$store.state.selectedBookingOffer)([
          "verifiedOffer",
          "paymentNotes",
        ]) || [];

      return paymentNotes;
    },
    paymentFrameworkUrl() {
      let draftId = this.$store.state.selectedBookingOffer.draftId;
      let url = APP_CONFIG.PAYMENT_FRAMEWORK_URL;

      if (draftId) {
        url += `&clientLogId=${draftId}`;
      }

      return url;
    },
  },
  components: {
    MsgBox,
    BookingBlock,
  },
};
</script>
